"use client";

import { useState, useEffect, Fragment } from "react";
import { toast, ToastContainer } from "react-toastify";
import dynamic from "next/dynamic";
import { Icon } from "@iconify/react";
import { useQuery } from "@tanstack/react-query";
import { generateJWTToken } from "@/utils/helpers/helperFunctions";
import { useRouter } from "next/navigation";
import { SwiperSlide } from "swiper/react";
import { fetchVideoBanner } from "@/utils/helpers/helperFunctions";
const VideoBanner = dynamic(
  () => import("@/components/new/general/VideoBanner"),
  { ssr: false }
);
const Button = dynamic(() => import("@/components/ui/Button"));

const LogoLoading = dynamic(() => import("@/components/ui/LogoLoading"));

// CountdownTimer component
function CountdownTimer({ start, end, hideEndedLabel, isPaused }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        // Only set up interval if not paused
        if (!isPaused) {
            const interval = setInterval(() => setNow(Date.now()), 1000);
            return () => clearInterval(interval);
        }
    }, [isPaused]);
    
    // If paused, show paused status
    if (isPaused) {
        return (
            <div className="flex items-center gap-2 mb-2 mt-1">
                <span className="text-xs font-semibold text-amber-400">Paused</span>
            </div>
        );
    }
    
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).getTime();
    let label = "";
    let diff = 0;
    if (now < startTime) {
        label = "Starts in";
        diff = startTime - now;
    } else if (now >= startTime && now < endTime) {
        label = "Ends in";
        diff = endTime - now;
    } else {
        label = "Ended";
        diff = 0;
    }
    function format(ms) {
        if (ms <= 0) return "00:00:00";
        const totalSeconds = Math.floor(ms / 1000);
        const h = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
        const s = String(totalSeconds % 60).padStart(2, '0');
        return `${h}:${m}:${s}`;
    }
    return (
        <div className="flex items-center gap-2 mb-2 mt-1">
            <span className={`text-xs font-semibold ${label === "Ends in" ? "text-yellow-400" : label === "Starts in" ? "text-yellow-400" : "text-metaxseed-blue"}`}>{label}</span>
            {label !== "Ended" && <span className="text-xs font-mono bg-metaxseed-content-gray text-white px-2 py-0.5 rounded">{format(diff)}</span>}
            {label === "Ended" && !hideEndedLabel && <span className="text-xs font-mono text-red-400">Ended</span>}
        </div>
    );
}

const Voting = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [pageSize] = useState(10);
    const [videoBannerURL, setVideoBannerURL] = useState(null);
    const [videoBannerMobileURL, setVideoBannerMobileURL] = useState(null);
    const router = useRouter();

    // Add a helper function to extract plain text from HTML
    const extractTextFromHtml = (html) => {
        if (!html) return '';
        
        if (typeof window === 'undefined') return '';
        
        try {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return doc.body.textContent || '';
        } catch (error) {
            console.error('Error parsing HTML:', error);
            return '';
        }
    };

    useEffect(() => {
        fetchVideoBanner("voting", setVideoBannerURL, setVideoBannerMobileURL);
    }, []);

    // Use React Query to fetch proposals
    const {
        data: proposalsData,
        isLoading: loading,
        error: queryError,
        refetch,
    } = useQuery({
        queryKey: ["proposals", currentPage, searchQuery, pageSize],
        queryFn: async () => {
            const token = await generateJWTToken();
            const response = await fetch(
                `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/get-all-proposals?page=${currentPage}&page_size=${pageSize}&search=${searchQuery}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            const data = await response.json();

            if (!data.success) {
                throw new Error(data.message || "Failed to fetch proposals");
            }

            return data;
        },
        staleTime: 30 * 1000, // Cache for 30 seconds
        retry: 2,
        onError: (error) => {
            console.error("Error fetching proposals:", error);
            toast.error(error.message || "Failed to fetch proposals");
        },
    });

    const proposals = proposalsData?.data?.proposals || [];
    const totalPages = proposalsData?.data?.pagination?.total_pages || 1;

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        // React Query will automatically refetch when currentPage or searchQuery changes
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        // React Query will automatically refetch when currentPage changes
    };

    return (
        <Fragment>
            <VideoBanner>
        <SwiperSlide>
          <div
            className={`swiperSlideDiv relative w-full h-[300px] md:h-[250px] transition-transform duration-[600ms] ease-in-out`}
          >
            <video
              preload="auto"
              src={
                videoBannerURL ||
                "https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/website%2Fpage_nodes%2Fbanner%2Fbanner_node_page.mp4?alt=media&token=780e26c4-17fa-43b3-8a7d-97bad56d116b"
              }
              autoPlay
              loop
              muted
              playsInline
              className="object-cover w-full h-full hidden md:block"
            />

            <video
              preload="auto"
              src={
                videoBannerMobileURL ||
                "https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/website%2Fpage_nodes%2Fbanner%2Fbanner_node_page_mobile.mp4?alt=media&token=7f3b3b3b-1b3b-4b3b-8b3b-9b3b3b3b3b3b"
              }
              autoPlay
              loop
              muted
              playsInline
              className="object-cover w-full h-full md:hidden"
            />

            <div className="absolute !pr-8 !pl-8 xl:container inset-x-[8%] 2xl:inset-x-[0%] block pt-2 pb-2 md:pb-4 text-left text-white top-1/2 transform -translate-y-1/2">
              <h1 className="text-xl md:text-2xl font-extrabold bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent mb-1 tracking-tight">MXS Games Voting System</h1>
              <p className="text-xs md:text-sm text-white max-w-xl">
                Participate in shaping the future of MXS Games! Browse active proposals, cast your vote, and make your voice heard in our community-driven governance platform.
              </p>
            </div>
          </div>
        </SwiperSlide>
      </VideoBanner>
       
        <div className="bg-metaxseed-dark-gray">
            <div className="max-w-7xl mx-auto md:px-8 px-4 py-6">
                <ToastContainer position="top-right" autoClose={5000} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable theme="dark" />
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <LogoLoading />
                    </div>
                ) : (
                    <>
                        {proposals.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="inline-flex rounded-full bg-yellow-100 p-4 mb-6">
                                    <Icon icon="ph:hourglass-medium-duotone" className="h-8 w-8 text-yellow-600" />
                                </div>
                                <h3 className="text-xl font-bold text-metaxseed-gray mb-2">No proposals available</h3>
                                <p className="text-metaxseed-gray mb-8 max-w-md mx-auto">There are currently no active proposals. Please check back later for new voting opportunities.</p>
                            </div>
                        ) : (
                            <>
                                <div className="mb-6">
                                    <form onSubmit={handleSearch} className="flex w-full max-w-md">
                                        <div className="relative w-full">
                                            <input
                                                type="text"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                placeholder="Search proposals..."
                                                className="w-full bg-[#1a1e24] text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-1 focus:ring-metaxseed-blue shadow border border-[#2a2f38] text-sm"
                                            />
                                            <button
                                                type="submit"
                                                className="absolute right-2 top-1/2 -translate-y-1/2 bg-metaxseed-blue text-white px-4 py-1.5 rounded-lg shadow hover:opacity-90 transition-colors"
                                            >
                                                <Icon icon="ph:magnifying-glass-bold" className="text-lg" />
                                            </button>
                                        </div>
                                    </form>
                                </div>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {proposals.map((proposal) => {
                                        const isEnded = new Date() > new Date(proposal.end_date);
                                        const isPaused = proposal.status === 'paused';
                                        return (
                                            <div
                                                key={proposal.id}
                                                className="bg-[#161a23] rounded-xl shadow-lg border border-[#2a2f38] flex flex-col p-6 h-full"
                                            >
                                                <div className="flex items-center justify-between w-full gap-3 mb-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-10 w-10 bg-[#23272e] text-white rounded-full flex items-center justify-center text-xl">
                                                            <Icon icon="ph:note-duotone" />
                                                        </div>
                                                        <span className="text-xs text-gray-400">Proposal</span>
                                                    </div>
                                                    {/* Always show CountdownTimer for upcoming and active proposals, only show date for ended */}
                                                    <>{isPaused ? (
                                                        <CountdownTimer start={proposal.start_date} end={proposal.end_date} hideEndedLabel={false} isPaused={true} />
                                                    ) : isEnded ? (
                                                        <span className="text-xs font-medium text-gray-300">{new Date(proposal.end_date).toLocaleString()}</span>
                                                    ) : (
                                                        <CountdownTimer start={proposal.start_date} end={proposal.end_date} hideEndedLabel={false} isPaused={false} />
                                                    )}</>
                                                </div>
                                                <div className="mb-4">
                                                    <div
                                                        className="font-bold text-white text-lg line-clamp-2 text-left"
                                                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                                        title={typeof proposal.question === 'string' ? extractTextFromHtml(proposal.question) : ''}
                                                        dangerouslySetInnerHTML={{ __html: proposal.question }}
                                                    />
                                                </div>
                                                <div className="flex items-center gap-2 mb-4 mt-auto">
                                                    {isPaused ? (
                                                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-200 flex items-center gap-1">
                                                            <Icon icon="ph:pause-circle-fill" className="text-amber-400" />
                                                            Paused
                                                        </span>
                                                    ) : new Date() < new Date(proposal.start_date) ? (
                                                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-orange-500 to-yellow-500 text-yellow-900">Upcoming</span>
                                                    ) : isEnded ? (
                                                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-400 to-orange-500 text-yellow-900">Ended</span>
                                                    ) : (
                                                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-200">Active</span>
                                                    )}
                                                    <span className="text-xs text-gray-400 ml-auto">
                                                        {new Date(proposal.created_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                {/* Stats Row */}
                                                <div className="flex items-center gap-3 mt-auto pt-4 border-t border-[#2a2f38]">
                                                    <div className="flex items-center gap-1 text-white font-semibold text-base">
                                                        <div className="h-6 w-6 bg-[#23272e] rounded-full flex items-center justify-center flex-shrink-0">
                                                            <Icon icon="ph:scales-bold" className="text-sm" />
                                                        </div>
                                                        <span>{(proposal.stats?.total_weight ?? 0).toLocaleString()}</span>
                                                        <span className="text-xs text-gray-400 font-normal flex-shrink-0">votes</span>
                                                    </div>
                                                    <div className="flex-1 min-w-0" />
                                                    <button
                                                        className="px-5 py-2 rounded-lg font-medium text-white bg-metaxseed-blue hover:opacity-90 transition-colors text-sm flex-shrink-0"
                                                        onClick={() => router.push(`/voting/${proposal.id}`)}
                                                    >
                                                        View
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                
                                {/* Pagination */}
                                <div className="flex justify-center items-center mt-6 mb-4 gap-2">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className={`px-3 py-1.5 rounded flex items-center gap-1 ${
                                            currentPage === 1
                                                ? 'bg-[#23272e] cursor-not-allowed text-gray-500'
                                                : 'bg-metaxseed-blue hover:bg-opacity-90 text-white'
                                        }`}
                                    >
                                        <Icon icon="ph:caret-left-bold" /> Prev
                                    </button>
                                    <div className="bg-[#1a1e24] border border-[#2a2f38] px-4 py-1.5 rounded">
                                        <span className="text-white text-sm">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className={`px-3 py-1.5 rounded flex items-center gap-1 ${
                                            currentPage === totalPages
                                                ? 'bg-[#23272e] cursor-not-allowed text-gray-500'
                                                : 'bg-metaxseed-blue hover:bg-opacity-90 text-white'
                                        }`}
                                    >
                                        Next <Icon icon="ph:caret-right-bold" />
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
        </Fragment>
    );
};

export default Voting;